import React, { useState, useEffect } from 'react';
import { FaTimes } from 'react-icons/fa';
import { toast } from 'react-toastify';
import { personalakteStyles as ps, colors, shadows } from '../../employees/styles/personalakteStyles';
import ProjectService, {
  Project,
  ProjectStatus,
  ProjectType,
  ProjectPriority,
  ProjectSource,
  CreateProjectPayload,
  PROJECT_STATUS_OPTIONS,
  PROJECT_TYPE_OPTIONS,
  PROJECT_PRIORITY_OPTIONS,
  PROJECT_SOURCE_OPTIONS,
} from '../services/ProjectService';

interface ProjectModalProps {
  isOpen: boolean;
  project: Project | null;
  partnerId: number;
  onClose: () => void;
  onSave: () => void;
}

export default function ProjectModal({
  isOpen,
  project,
  partnerId,
  onClose,
  onSave,
}: ProjectModalProps): React.JSX.Element | null {
  const [formData, setFormData] = useState({
    name: '',
    reference: '',
    status: 'LEAD' as ProjectStatus,
    street: '',
    house_no: '',
    postal_code: '',
    city: '',
    country: '',
    notes: '',
    project_type: '' as ProjectType | '',
    priority: 'NORMAL' as ProjectPriority,
    source: '' as ProjectSource | '',
    planned_start: '',
    planned_end: '',
  });
  const [saving, setSaving] = useState(false);

  const isEdit = !!project;

  useEffect(() => {
    if (project) {
      setFormData({
        name: project.name || '',
        reference: project.reference || '',
        status: project.status || 'LEAD',
        street: project.street || '',
        house_no: project.house_no || '',
        postal_code: project.postal_code || '',
        city: project.city || '',
        country: project.country || '',
        notes: project.notes || '',
        project_type: project.project_type || '',
        priority: project.priority || 'NORMAL',
        source: project.source || '',
        planned_start: project.planned_start || '',
        planned_end: project.planned_end || '',
      });
    } else {
      setFormData({
        name: '',
        reference: '',
        status: 'LEAD',
        street: '',
        house_no: '',
        postal_code: '',
        city: '',
        country: '',
        notes: '',
        project_type: '',
        priority: 'NORMAL',
        source: '',
        planned_start: '',
        planned_end: '',
      });
    }
  }, [project, partnerId, isOpen]);

  const handleChange = (key: string, value: string) => {
    setFormData((prev) => ({ ...prev, [key]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.name?.trim()) {
      toast.error('Project name is required');
      return;
    }

    // Build explicit payload with correct keys
    const payload: CreateProjectPayload = {
      partner: partnerId,
      name: formData.name.trim(),
      reference: formData.reference?.trim() || '',
      status: formData.status || 'LEAD',
      street: formData.street?.trim() || '',
      house_no: formData.house_no?.trim() || '',
      postal_code: formData.postal_code?.trim() || '',
      city: formData.city?.trim() || '',
      country: formData.country?.trim() || 'DE',
      notes: formData.notes?.trim() || '',
      project_type: formData.project_type || undefined,
      priority: formData.priority || 'NORMAL',
      source: formData.source || undefined,
      planned_start: formData.planned_start || undefined,
      planned_end: formData.planned_end || undefined,
    };

    setSaving(true);
    try {
      if (isEdit && project) {
        const { partner, ...updatePayload } = payload;
        await ProjectService.update(project.id, updatePayload);
        toast.success('Project updated');
      } else {
        await ProjectService.create(payload);
        toast.success('Project created');
      }
      onSave();
    } catch (err: any) {
      console.error('Failed to save project:', err);
      console.error('Backend says:', err?.response?.data);

      const responseData = err?.response?.data;
      let message = 'Save failed';

      if (responseData) {
        if (responseData.detail) {
          message = responseData.detail;
        } else if (responseData.non_field_errors) {
          message = responseData.non_field_errors.join(', ');
        } else if (typeof responseData === 'object') {
          const errors = Object.entries(responseData)
            .map(([field, msgs]) => `${field}: ${Array.isArray(msgs) ? msgs.join(', ') : msgs}`)
            .join('; ');
          if (errors) message = errors;
        }
      }

      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0,0,0,0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: colors.bgCard,
          borderRadius: '12px',
          width: '90%',
          maxWidth: '600px',
          maxHeight: '90vh',
          overflow: 'auto',
          boxShadow: shadows.elevated,
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '20px 24px',
            borderBottom: `1px solid ${colors.border}`,
          }}
        >
          <h2 style={{ margin: 0, fontSize: '18px', fontWeight: 600, color: colors.navy }}>
            {isEdit ? 'Edit project' : 'New project'}
          </h2>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: '8px',
              color: colors.textMuted,
            }}
          >
            <FaTimes size={18} />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} style={{ padding: '24px' }}>
          {/* Name */}
          <div style={{ marginBottom: '20px' }}>
            <label style={ps.labelStyle}>
              Project name <span style={{ color: colors.danger }}>*</span>
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => handleChange('name', e.target.value)}
              placeholder="e.g. Office Renovation"
              style={ps.inputStyle}
              required
            />
          </div>

          {/* Reference + Status */}
          <div style={{ display: 'flex', gap: '16px', marginBottom: '20px' }}>
            <div style={{ flex: 1 }}>
              <label style={ps.labelStyle}>Referenz / Projektnummer</label>
              <input
                type="text"
                value={formData.reference}
                onChange={(e) => handleChange('reference', e.target.value)}
                placeholder="PRJ-2024-001"
                style={ps.inputStyle}
              />
            </div>
            <div style={{ flex: 1 }}>
              <label style={ps.labelStyle}>Status</label>
              <select
                value={formData.status}
                onChange={(e) => handleChange('status', e.target.value as ProjectStatus)}
                style={{ ...ps.inputStyle, background: colors.bgCard }}
              >
                {PROJECT_STATUS_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Project Type + Priority */}
          <div style={{ display: 'flex', gap: '16px', marginBottom: '20px' }}>
            <div style={{ flex: 1 }}>
              <label style={ps.labelStyle}>Projektart</label>
              <select
                value={formData.project_type}
                onChange={(e) => handleChange('project_type', e.target.value)}
                style={{ ...ps.inputStyle, background: colors.bgCard }}
              >
                <option value="">-- Auswählen --</option>
                {PROJECT_TYPE_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>
            <div style={{ flex: 1 }}>
              <label style={ps.labelStyle}>Priorität</label>
              <select
                value={formData.priority}
                onChange={(e) => handleChange('priority', e.target.value as ProjectPriority)}
                style={{ ...ps.inputStyle, background: colors.bgCard }}
              >
                {PROJECT_PRIORITY_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Source */}
          <div style={{ marginBottom: '20px' }}>
            <label style={ps.labelStyle}>Quelle</label>
            <select
              value={formData.source}
              onChange={(e) => handleChange('source', e.target.value)}
              style={{ ...ps.inputStyle, background: colors.bgCard }}
            >
              <option value="">-- Auswählen --</option>
              {PROJECT_SOURCE_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>

          {/* Planned Dates */}
          <div style={{ display: 'flex', gap: '16px', marginBottom: '20px' }}>
            <div style={{ flex: 1 }}>
              <label style={ps.labelStyle}>Geplanter Start</label>
              <input
                type="date"
                value={formData.planned_start}
                onChange={(e) => handleChange('planned_start', e.target.value)}
                style={ps.inputStyle}
              />
            </div>
            <div style={{ flex: 1 }}>
              <label style={ps.labelStyle}>Geplantes Ende</label>
              <input
                type="date"
                value={formData.planned_end}
                onChange={(e) => handleChange('planned_end', e.target.value)}
                style={ps.inputStyle}
              />
            </div>
          </div>

          {/* Address */}
          <div style={{ marginBottom: '20px' }}>
            <label style={ps.labelStyle}>Project address (if different)</label>
            <div style={{ display: 'flex', gap: '16px', marginBottom: '12px' }}>
              <div style={{ flex: 2 }}>
                <input
                  type="text"
                  value={formData.street}
                  onChange={(e) => handleChange('street', e.target.value)}
                  placeholder="Street"
                  style={ps.inputStyle}
                />
              </div>
              <div style={{ flex: 1 }}>
                <input
                  type="text"
                  value={formData.house_no}
                  onChange={(e) => handleChange('house_no', e.target.value)}
                  placeholder="No."
                  style={ps.inputStyle}
                />
              </div>
            </div>
            <div style={{ display: 'flex', gap: '16px' }}>
              <div style={{ flex: 1 }}>
                <input
                  type="text"
                  value={formData.postal_code}
                  onChange={(e) => handleChange('postal_code', e.target.value)}
                  placeholder="Postal code"
                  style={ps.inputStyle}
                />
              </div>
              <div style={{ flex: 2 }}>
                <input
                  type="text"
                  value={formData.city}
                  onChange={(e) => handleChange('city', e.target.value)}
                  placeholder="City"
                  style={ps.inputStyle}
                />
              </div>
            </div>
          </div>

          {/* Notes */}
          <div style={{ marginBottom: '24px' }}>
            <label style={ps.labelStyle}>Notes</label>
            <textarea
              value={formData.notes}
              onChange={(e) => handleChange('notes', e.target.value)}
              rows={3}
              placeholder="Internal notes about this project..."
              style={{ ...ps.inputStyle, resize: 'vertical' }}
            />
          </div>

          {/* Actions */}
          <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
            <button
              type="button"
              onClick={onClose}
              style={{ ...ps.buttonSecondary }}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              style={saving
                ? { ...ps.buttonPrimary, background: colors.border, cursor: 'not-allowed', boxShadow: 'none' }
                : { ...ps.buttonPrimary }
              }
            >
              {saving ? 'Saving...' : isEdit ? 'Update' : 'Create'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
